import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';

import { webFormsApi } from '../../axios';
import {
  AnyQuestion,
  MetadataQuestion,
  NestableLocationsData,
  Question,
  QuestionType,
  RepeatableQuestion,
  RepeatableQuestionAnswerValue,
  RepeatedQuestionValue,
  SkippedInRepeatable,
  WebFormsState,
} from '../../types';
import { normalizeMetadataOptionWithCommentsValue } from '../../utils/webForms';
import { RootState } from '../store';

const initialState: WebFormsState = {
  formId: '',
  formName: '',
  formCategory: '',
  formDescription: '',
  questions: [],
  metadata: {},
  answers: {},
  pendingUploadsMap: {},
  skippedQuestions: [],
  skippedInRepeatable: {},
  loading: false,
  sections: [],
  currentSection: 0,
  findingsCreated: [],
  automatedSignatureOnResponse: false,
  authorMustSign: false,
  unpublished: false,
  filterAssetsByLocation: false,
  lockedAssetQuestionIds: [],
};

const complexAnswerTypes = [
  'select_check_metadata',
  'select_text_metadata',
  'select_number_metadata',
  'select_option_metadata',
  'attachment',
  'date',
];

function answersForQuestions(questions: WebFormsState['questions']) {
  if (!questions) return {};
  return questions.reduce((acc, question) => {
    const value = question.type === 'table' ? { 0: {} } : undefined;
    return {
      ...acc,
      [question.id]: {
        value,
      },
    };
  }, {});
}

/**
 * When the loaded answer is for a nestable-locations question, the server
 * stores `value` as just the location name and exposes the code
 * separately under `nestable_locations.selected[metadata_id].code`. Skip-logic
 * configured by the form builder is stored as "Name [code]", so the bare name
 * never matches and skippable questions get incorrectly hidden on edit.
 *
 * Reconstruct the value to "Name [code]" in place when the data is available.
 * Returns true if the value was rewritten, so the caller can persist the
 * shallow-copied container back to the answers map.
 */
function appendLocationCodeIfNeeded(target: {
  value?: unknown;
  metadata_id?: number;
  nestable_locations?: NestableLocationsData;
}): boolean {
  if (typeof target.value !== 'string' || target.value.length === 0) return false;
  if (target.metadata_id == null) return false;
  const code = target.nestable_locations?.selected?.[String(target.metadata_id)]?.code;
  if (!code) return false;
  if (target.value.endsWith(`[${code}]`)) return false;
  target.value = `${target.value} [${code}]`;
  return true;
}

/** Mutation wrapper around `normalizeMetadataOptionWithCommentsValue`. */
function normalizeMetadataOptionWithCommentsIfNeeded(
  target: { value?: unknown },
  question: { type?: string; metadata_code?: string } | undefined,
  metadata: WebFormsState['metadata'],
): boolean {
  const normalizedValue = normalizeMetadataOptionWithCommentsValue(
    target.value,
    question,
    metadata,
  );
  if (normalizedValue === null) return false;
  target.value = normalizedValue;
  return true;
}

function formatAnswerData(
  answers: WebFormsState['answers'],
  questions: WebFormsState['questions'],
  metadata: WebFormsState['metadata'],
) {
  Object.keys(answers).forEach((questionId) => {
    const answer = { ...answers[questionId] };
    answer.edited = false;
    const question = questions.find((q) => q.id === questionId);
    if (
      appendLocationCodeIfNeeded(answer) ||
      normalizeMetadataOptionWithCommentsIfNeeded(answer, question, metadata)
    ) {
      answers[questionId] = answer;
    }
    if (question?.type === 'table') {
      const tableValue = answer.value as RepeatableQuestionAnswerValue;
      Object.keys(tableValue).forEach((rowId) => {
        const row = tableValue[parseInt(rowId)];
        const newRowValue = question.questions.reduce((acc, subQuestion, subQuestionIndex) => {
          const subAnswer = row[parseInt(subQuestion.id)];
          if (subAnswer?.value) {
            const column = {
              ...subAnswer,
              id: subQuestion.id,
              name: subQuestion.name,
              type: subQuestion.type,
              question_name: question.name,
              sectionId: question.sectionId,
            };
            // if subQuestion is of a complex type, add the metadata attribute to the column
            if (complexAnswerTypes.includes(subQuestion.type)) {
              const metadataQuestion = subQuestion as MetadataQuestion;
              column.metadata = metadataQuestion.metadata;
              column.metadata_code = metadataQuestion.metadata;
            }
            normalizeMetadataOptionWithCommentsIfNeeded(column, subQuestion, metadata);
            appendLocationCodeIfNeeded(column);
            acc[subQuestionIndex] = column;
          }
          return acc;
        }, {} as Record<number, RepeatedQuestionValue>);
        tableValue[parseInt(rowId)] = newRowValue;
      });
    }
  });
  return answers;
}

const evaluateContains = (skipDependentAnswer: unknown, skipLogicValue: unknown) => {
  return (skipDependentAnswer || '')
    .toString()
    .toLowerCase()
    .includes(skipLogicValue.toString().toLowerCase());
};

const evaluateEquality = (
  question: Pick<Question, 'skipLogicValue'>,
  skipDependentAnswer: unknown,
  skipLogicQuestionType: QuestionType,
) => {
  if (complexAnswerTypes.includes(skipLogicQuestionType)) {
    if (skipLogicQuestionType === 'date') return skipDependentAnswer.toString().length === 0;
    else if (skipLogicQuestionType === 'select_check_metadata') {
      const answer = skipDependentAnswer as Record<string, boolean>;
      return Object.values(answer).length === 0;
    } else {
      const answer = skipDependentAnswer as Record<string, string>;
      return Object.values(answer).length === 0;
    }
  } else {
    return skipDependentAnswer === question.skipLogicValue;
  }
};

type QuestionWithSkipLogic = Pick<Question, 'skipLogic' | 'skipLogicType' | 'skipLogicValue'>;

function evaluateShouldBeSkipped(
  question: QuestionWithSkipLogic,
  skipDependentAnswer: unknown,
  skipLogicQuestionType?: QuestionType,
): boolean {
  if (question.skipLogic !== '1') return false;
  if (question.skipLogicType === 'contains' || question.skipLogicType === 'not_contains') {
    const containsValue = evaluateContains(skipDependentAnswer, question.skipLogicValue ?? '');
    return question.skipLogicType === 'contains' ? containsValue : !containsValue;
  } else if (question.skipLogicType === 'equal' || question.skipLogicType === 'dif') {
    const match = evaluateEquality(question, skipDependentAnswer, skipLogicQuestionType ?? 'text');
    return question.skipLogicType === 'equal' ? match : !match;
  }
  return false;
}

/**
 * Computes which top-level questions should be hidden based on skip logic.
 * For each question with skipLogic enabled, evaluates the dependent answer
 * (from another question) and determines if the condition (contains, equal, etc.)
 * is met. When met, the question is considered skipped.
 *
 * @param questions - All form questions (top-level only).
 * @param answers - Current answers keyed by question id.
 * @returns Array of question ids that should be hidden from the form.
 */
const getSkippedQuestions = (
  questions: WebFormsState['questions'],
  answers: WebFormsState['answers'],
) => {
  return questions.reduce((acc, question) => {
    const skipDependentAnswer = answers[question.skipLogicQuestion]?.value ?? '';
    const skipLogicQuestionType = questions.find((q) => question.skipLogicQuestion === q.id)?.type;
    if (evaluateShouldBeSkipped(question, skipDependentAnswer, skipLogicQuestionType)) {
      acc.push(question.id);
    }
    return acc;
  }, [] as string[]);
};

/**
 * Extracts the answer value of the "trigger" question for skip logic evaluation
 * within a repeatable (table) group. Answers in repeatables are nested as
 * value[groupIndex][questionIndex]; this returns the value at the given indices.
 *
 * @param tableValue - The repeatable answer structure (groupIndex -> questionIndex -> entry).
 * @param groupIndex - Which row/group to read from.
 * @param skipLogicQuestionIndex - Index of the sub-question that triggers the skip logic.
 * @returns The raw answer value, or empty string if not present.
 */
function getSkipDependentAnswerForRepeatable(
  tableValue: RepeatableQuestionAnswerValue,
  groupIndex: number,
  skipLogicQuestionIndex: number,
): unknown {
  const row = tableValue[groupIndex];
  const entry = row?.[skipLogicQuestionIndex] as RepeatedQuestionValue | undefined;
  return entry?.value ?? '';
}

/**
 * Computes which sub-questions within repeatable (table) groups should be hidden
 * per row, based on skip logic. Each table can have multiple groups (rows), and
 * each group is evaluated independently: a sub-question may be skipped in group 0
 * but visible in group 1 depending on the answers in that row.
 *
 * Iterates over table questions, then over sub-questions with skipLogic, then over
 * each group. For each (table, group, subQuestion), reads the trigger answer from
 * the same row and evaluates the skip condition.
 *
 * @param questions - All form questions (includes table questions with nested sub-questions).
 * @param answers - Current answers; table answers use value[groupIndex][questionIndex].
 * @returns Nested structure: tableId -> groupIndex -> subQuestionId[], indicating
 *          which sub-questions to hide in each group of each table.
 */
const getSkippedInRepeatable = (
  questions: WebFormsState['questions'],
  answers: WebFormsState['answers'],
): SkippedInRepeatable => {
  const result: SkippedInRepeatable = {};
  const tableQuestions = questions.filter((q): q is RepeatableQuestion => q.type === 'table');

  for (const tableQuestion of tableQuestions) {
    const subQuestions: AnyQuestion[] = tableQuestion.questions ?? [];
    const tableValue = answers[tableQuestion.id]?.value as
      | RepeatableQuestionAnswerValue
      | undefined;

    if (!tableValue) continue;

    for (const subQuestion of subQuestions) {
      if (subQuestion.skipLogic !== '1' || !subQuestion.skipLogicQuestion) continue;

      const skipLogicQuestionIndex = subQuestions.findIndex(
        (sq: AnyQuestion) => sq.id === subQuestion.skipLogicQuestion,
      );
      if (skipLogicQuestionIndex === -1) continue;

      const skipLogicQuestionType = subQuestions[skipLogicQuestionIndex]?.type;

      for (const groupKey of Object.keys(tableValue)) {
        const groupIndex = parseInt(groupKey);
        const skipDependentAnswer = getSkipDependentAnswerForRepeatable(
          tableValue,
          groupIndex,
          skipLogicQuestionIndex,
        );

        if (evaluateShouldBeSkipped(subQuestion, skipDependentAnswer, skipLogicQuestionType)) {
          if (!result[tableQuestion.id]) result[tableQuestion.id] = {};
          if (!result[tableQuestion.id][groupIndex]) {
            result[tableQuestion.id][groupIndex] = [];
          }
          result[tableQuestion.id][groupIndex].push(subQuestion.id);
        }
      }
    }
  }

  return result;
};

const getUniqueSections = (questions: WebFormsState['questions']) => {
  return Array.from(new Set(questions?.map((question) => question.label)));
};

/**
 * Removes answer entries for skipped sub-questions from a repeatable (table) value.
 * Used before submission and when computing isEmpty / getAnswersWithoutSkipped,
 * so that hidden sub-questions do not appear in the payload or affect validation.
 * Returns a shallow copy; only the affected group rows are modified.
 *
 * @param value - The raw repeatable answer (groupIndex -> questionIndex -> entry).
 * @param tableQuestion - The table question, used to map subQuestionId -> questionIndex.
 * @param skippedInRepeatable - Structure indicating which sub-questions are skipped per group.
 * @returns A copy of value with entries for skipped sub-questions removed per group.
 */
function stripSkippedFromRepeatableValue(
  value: RepeatableQuestionAnswerValue,
  tableQuestion: Question,
  skippedInRepeatable: SkippedInRepeatable,
): RepeatableQuestionAnswerValue {
  const tableSkipped = skippedInRepeatable[tableQuestion.id];
  if (!tableSkipped || tableQuestion.type !== 'table') return value;

  const subQuestions = tableQuestion.questions ?? [];
  const result = { ...value };

  Object.keys(tableSkipped).forEach((groupKey) => {
    const groupIndex = parseInt(groupKey);
    const skippedSubIds = tableSkipped[groupIndex];
    if (!skippedSubIds || !result[groupIndex]) return;

    const row = { ...result[groupIndex] };
    skippedSubIds.forEach((subQuestionId) => {
      const questionIndex = subQuestions.findIndex((sq) => sq.id === subQuestionId);
      if (questionIndex >= 0) delete row[questionIndex];
    });
    result[groupIndex] = row;
  });

  return result;
}

const getTemplateForm = createAsyncThunk(
  'getTemplateForm',
  async ({ formId, shareId }: { formId: string; shareId: string }) => {
    return await webFormsApi.getTemplateForm(formId, shareId);
  },
);

const getForm = createAsyncThunk(
  'getForm',
  async ({ formId, shareToken }: { formId: string; shareToken: string }) => {
    return await webFormsApi.getForm(formId, shareToken);
  },
);

const getFormAnswer = createAsyncThunk(
  'getFormAnswer',
  async ({ formAnswerId }: { formAnswerId: string }) => {
    return await webFormsApi.getFormAnswer(formAnswerId);
  },
);

export const webFormsSlice = createSlice({
  name: 'webForms',
  initialState: initialState,
  reducers: {
    updateAnswerValue: (state, action) => {
      const { questionId, value, editMode } = action.payload;
      state.answers = {
        ...state.answers,
        [questionId]: {
          ...state.answers[questionId],
          value,
          edited: editMode,
        },
      };
      state.skippedQuestions = getSkippedQuestions(state.questions, state.answers);
    },
    updateRepeatableAnswerValue: (state, action) => {
      const { questionId, value, groupIndex, questionIndex, editMode } = action.payload;
      const answer = state.answers[questionId];
      const tableValue = answer.value as RepeatableQuestionAnswerValue;
      const row = tableValue[groupIndex];
      const newRowValue = { ...row, [questionIndex]: value };
      if (isEmpty(value)) {
        delete newRowValue[questionIndex];
      }
      tableValue[groupIndex] = newRowValue;
      state.answers = {
        ...state.answers,
        [questionId]: {
          ...state.answers[questionId],
          value: tableValue,
          edited: editMode,
        },
      };
      state.skippedInRepeatable = getSkippedInRepeatable(state.questions, state.answers);
    },
    addRepeatableRow: (state, action) => {
      const { questionId, editMode } = action.payload;
      const answer = state.answers[questionId];
      const tableValue = answer.value as RepeatableQuestionAnswerValue;
      tableValue[Object.keys(tableValue).length] = {};
      state.answers = {
        ...state.answers,
        [questionId]: {
          ...state.answers[questionId],
          value: tableValue,
          edited: editMode,
        },
      };
      state.skippedInRepeatable = getSkippedInRepeatable(state.questions, state.answers);
    },
    removeRepeatableRow: (state, action) => {
      const { questionId, groupIndex, editMode } = action.payload;
      const answer = state.answers[questionId];
      const tableValue = answer.value as RepeatableQuestionAnswerValue;
      delete tableValue[groupIndex];
      state.answers = {
        ...state.answers,
        [questionId]: {
          ...state.answers[questionId],
          value:
            Object.keys(tableValue).length === 0 ? { 0: {} } : { ...Object.values(tableValue) },
          edited: editMode,
        },
      };
      state.skippedInRepeatable = getSkippedInRepeatable(state.questions, state.answers);
    },
    updateAnswerError: (state, action) => {
      const { questionId, error } = action.payload;
      state.answers = {
        ...state.answers,
        [questionId]: {
          ...state.answers[questionId],
          error,
        },
      };
    },

    updateAnswerLoading: (state, action) => {
      const { questionId, loading } = action.payload;
      state.answers = {
        ...state.answers,
        [questionId]: {
          ...state.answers[questionId],
          loading,
        },
      };
    },

    // Tracks pending uploads for standalone questions (photo, file) via answers[questionId].pendingUploads
    updatePendingUploads: (state, action) => {
      const { questionId, delta } = action.payload;
      const current = state.answers[questionId]?.pendingUploads ?? 0;
      state.answers = {
        ...state.answers,
        [questionId]: {
          ...state.answers[questionId],
          pendingUploads: Math.max(0, current + delta),
        },
      };
    },

    // Tracks pending uploads for composite-keyed contexts (checklist metadata sub-questions,
    // repeatable sub-questions) via pendingUploadsMap with keys like `instanceKey:metadataItemName`
    updatePendingUploadsMap: (state, action) => {
      const { key, delta }: { key: string; delta: number } = action.payload;
      const current = state.pendingUploadsMap[key] ?? 0;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        const rest = { ...state.pendingUploadsMap };
        delete rest[key];
        state.pendingUploadsMap = rest;
      } else {
        state.pendingUploadsMap = { ...state.pendingUploadsMap, [key]: next };
      }
    },

    updateAnswersErrors: (state, action) => {
      const { answersErrors }: { answersErrors: { questionId: string; error: unknown }[] } =
        action.payload;
      state.answers = answersErrors.reduce((acc, { questionId, error }) => {
        return {
          ...acc,
          [questionId]: {
            ...state.answers[questionId],
            error,
          },
        };
      }, state.answers);
    },

    /**
     * Records which asset questions the `assetId` prefill filled in and locked. Kept in
     * the store, not in `webForm.tsx`, because the location sweep in `setAnswerValue`
     * has to leave them alone and it cannot see component state (DS-3776).
     */
    setLockedAssetQuestionIds: (state, action: PayloadAction<string[]>) => {
      state.lockedAssetQuestionIds = action.payload;
    },

    resetForm: (state) => {
      state.answers = answersForQuestions(state.questions);
      state.pendingUploadsMap = {};
      state.currentSection = 0;
      state.skippedQuestions = getSkippedQuestions(state.questions, state.answers);
      state.skippedInRepeatable = getSkippedInRepeatable(state.questions, state.answers);
      // findingsCreated is intentionally not cleared here: entries are scoped per
      // (formId, formAnswerId) and only removed by removeFindingsForForm after the
      // submit batch links them, so resetting before submit cannot orphan a finding.
    },

    nextSection: (state) => {
      if (state.currentSection < state.sections.length - 1) {
        state.currentSection += 1;
      }
    },

    previousSection: (state) => {
      if (state.currentSection > 0) {
        state.currentSection -= 1;
      }
    },

    addFinding: (
      state,
      action: PayloadAction<{ findingId: string; formId: string; formAnswerId: string | null }>,
    ) => {
      // Store the (formId, formAnswerId) scope at creation time so the submit
      // batch can later link this finding only to the form it was created from.
      state.findingsCreated.push(action.payload);
    },

    removeFindingsForForm: (
      state,
      action: PayloadAction<{ formId: string; formAnswerId: string | null }>,
    ) => {
      // Called after the submit/edit batch has linked the matching findings, so
      // they don't carry over into a subsequent submission on a different form.
      const { formId, formAnswerId } = action.payload;
      state.findingsCreated = state.findingsCreated.filter(
        (entry) => !(entry.formId === formId && entry.formAnswerId === formAnswerId),
      );
    },

    restoreDraftFindings: (
      state,
      action: PayloadAction<{ formId: string; findingIds: string[] }>,
    ) => {
      // The draft is reloaded only for create-mode submissions of a single form,
      // so all restored entries share formId and formAnswerId=null. Drop any
      // existing matching entries first so a double-loadDraft cannot duplicate ids.
      const { formId, findingIds } = action.payload;
      state.findingsCreated = state.findingsCreated.filter(
        (entry) => !(entry.formId === formId && entry.formAnswerId === null),
      );
      findingIds.forEach((findingId) => {
        state.findingsCreated.push({ findingId, formId, formAnswerId: null });
      });
    },

    loadDraftAnswers: (state, action: PayloadAction<Record<string, { value: unknown }>>) => {
      Object.entries(action.payload).forEach(([questionId, { value }]) => {
        if (state.answers[questionId]) {
          state.answers[questionId] = { ...state.answers[questionId], value };
        }
      });
      state.skippedQuestions = getSkippedQuestions(state.questions, state.answers);
      state.skippedInRepeatable = getSkippedInRepeatable(state.questions, state.answers);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTemplateForm.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getTemplateForm.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getTemplateForm.fulfilled, (state, action) => {
      state.formName = action.payload.form_name;
      state.formCategory = action.payload.form_category;
      state.formDescription = action.payload.description;
      state.questions = action.payload.questions;
      state.metadata = action.payload.metadata;
      state.answers = answersForQuestions(action.payload.questions);
      state.pendingUploadsMap = {};
      state.skippedQuestions = getSkippedQuestions(action.payload.questions, state.answers);
      state.skippedInRepeatable = getSkippedInRepeatable(action.payload.questions, state.answers);
      state.loading = false;
    });

    builder.addCase(getForm.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getForm.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getForm.fulfilled, (state, action) => {
      state.formId = action.meta.arg.formId;
      state.formName = action.payload.form_name;
      state.questions = action.payload.questions;
      state.metadata = action.payload.metadata;
      state.sections = getUniqueSections(action.payload.questions);
      state.answers = answersForQuestions(action.payload.questions);
      state.pendingUploadsMap = {};
      state.skippedQuestions = getSkippedQuestions(action.payload.questions, state.answers);
      state.skippedInRepeatable = getSkippedInRepeatable(action.payload.questions, state.answers);
      state.automatedSignatureOnResponse = action.payload.automated_signature_on_response;
      state.authorMustSign = action.payload.author_must_sign;
      state.unpublished = action.payload.unpublished;
      // Defaults to false rather than leaving the previous form's value behind: this
      // slice is reused across forms and an absent field means an older backend.
      state.filterAssetsByLocation = action.payload.filter_assets_by_location === true;
      // Cleared with the form: the prefill effect recomputes it for the form that just
      // loaded, and carrying ids from the previous one would shield questions that no
      // longer exist while leaving the real ones sweepable.
      state.lockedAssetQuestionIds = [];
      state.currentSection = 0;
      state.loading = false;
    });
    builder.addCase(getFormAnswer.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getFormAnswer.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getFormAnswer.fulfilled, (state, action) => {
      state.answers = {
        ...state.answers,
        ...formatAnswerData(action.payload, state.questions, state.metadata),
      };
      state.skippedQuestions = getSkippedQuestions(state.questions, state.answers);
      state.skippedInRepeatable = getSkippedInRepeatable(state.questions, state.answers);
      state.currentSection = 0;
      state.loading = false;
    });
  },
});

export const {
  updateAnswerValue,
  updateRepeatableAnswerValue,
  updateAnswerError,
  updateAnswerLoading,
  updatePendingUploads,
  updatePendingUploadsMap,
  updateAnswersErrors,
  resetForm,
  nextSection,
  previousSection,
  addFinding,
  removeFindingsForForm,
  restoreDraftFindings,
  addRepeatableRow,
  removeRepeatableRow,
  loadDraftAnswers,
  setLockedAssetQuestionIds,
} = webFormsSlice.actions;

export { getForm, getFormAnswer, getTemplateForm };

export const alwaysUnsupportedQuestionTypes = ['barcode', 'multi_photo'];

export const isUnsupportedQuestion = (question: Question): boolean => {
  if (alwaysUnsupportedQuestionTypes.includes(question.type)) {
    return true;
  }

  if (question.type === 'attachment' && question.formats?.includes('Voice Audio Recording')) {
    return true;
  }

  return false;
};

const stateAnswers = (state: RootState) => state.webForms.answers;
const stateQuestions = (state: RootState) => state.webForms.questions;
const stateSkippedQuestions = (state: RootState) => state.webForms.skippedQuestions;
const stateSkippedInRepeatable = (state: RootState) => state.webForms.skippedInRepeatable;

export const getQuestions = createSelector(
  [stateQuestions, stateSkippedQuestions],
  (questions, skippedQuestions) => {
    return questions.filter(
      (question) => !skippedQuestions.includes(question.id) && !isUnsupportedQuestion(question),
    );
  },
);

export const getQuestionsWithSkipped = createSelector([stateQuestions], (questions) => {
  return questions.filter((question) => !isUnsupportedQuestion(question));
});

export function getAnswer(state: RootState, questionId: string) {
  return state.webForms.answers[questionId];
}

export function isSubQuestionSkipped(
  state: RootState,
  tableId: string,
  groupIndex: number,
  subQuestionId: string,
): boolean {
  const skippedInRepeatable = stateSkippedInRepeatable(state);
  const tableSkipped = skippedInRepeatable[tableId];
  if (!tableSkipped) return false;
  const groupSkipped = tableSkipped[groupIndex];
  if (!groupSkipped) return false;
  return groupSkipped.includes(subQuestionId);
}

export const getAnswersWithoutSkipped = createSelector(
  [stateAnswers, stateSkippedQuestions, stateSkippedInRepeatable, stateQuestions],
  (answers, skippedQuestions, skippedInRepeatable, questions) => {
    return Object.keys(answers)
      .filter((id) => !skippedQuestions.includes(id))
      .reduce((result, key) => {
        const answer = answers[key];
        const question = questions.find((q) => q.id === key);
        let value = answer.value;

        if (
          question?.type === 'table' &&
          Object.keys(skippedInRepeatable).length > 0 &&
          skippedInRepeatable[key]
        ) {
          value = stripSkippedFromRepeatableValue(
            value as RepeatableQuestionAnswerValue,
            question,
            skippedInRepeatable,
          );
        }

        result[key] = { ...answer, value };
        return result;
      }, {} as WebFormsState['answers']);
  },
);

export function getMetadata(state: RootState, metadataId: string) {
  return state.webForms.metadata[metadataId];
}

export default webFormsSlice.reducer;
